import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ColDef } from 'ag-grid-enterprise'
import { AccountMapGridRow } from '../../utils/accountMapUtils'
import { StatementRow } from '../../../../types/statementRow/StatementRow'
import StatementRowGroupCell from './StatementRowGroupCell'
import { ExpandAllHeaderRenderer } from '../../../../shared/components/custom/Grid/cellRenderers/ExpandAllHeaderRenderer'

interface UseAccountMapGridCallbacksParams {
  statementRows?: StatementRow[]
}

export function useAccountMapGridCallbacks({ statementRows }: UseAccountMapGridCallbacksParams) {
  const { t } = useTranslation()

  // Map statementRowId to StatementRow for quick lookup
  const statementRowsById = useMemo(() => {
    if (!statementRows) return new Map<number, StatementRow>()
    return new Map(statementRows.map(row => [row.id, row]))
  }, [statementRows])

  const getStatementRowName = useCallback(
    (statementRowId: number): string => {
      // First try to get from statementRows prop
      const row = statementRowsById.get(statementRowId)
      if (row) {
        // Use translation based on report type
        const translationKey =
          row.report === 'INCOME_STATEMENT' ? `incomeStatement:${row.id}` : `balanceSheet:${row.id}`
        return t(translationKey, row.title || String(row.id))
      }

      // Fallback: try both translation namespaces
      const balanceSheetKey = `balanceSheet:${statementRowId}`
      const incomeStatementKey = `incomeStatement:${statementRowId}`

      const balanceSheetName = t(balanceSheetKey, '')
      if (balanceSheetName && balanceSheetName !== balanceSheetKey) {
        return balanceSheetName
      }

      const incomeStatementName = t(incomeStatementKey, '')
      if (incomeStatementName && incomeStatementName !== incomeStatementKey) {
        return incomeStatementName
      }

      return String(statementRowId)
    },
    [t, statementRowsById]
  )

  // Shared predicate: a group row is expandable only if it exists in statementRowsById and is not a parent
  const isExpandableGroup = useCallback(
    (key: string | null | undefined): boolean => {
      if (!key) return false
      const statementRow = statementRowsById.get(Number(key))
      return statementRow != null && !statementRow.isParent
    },
    [statementRowsById]
  )

  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      headerName: t('accountMapPage:statementRow', 'Statement Row'),
      headerComponent: ExpandAllHeaderRenderer,
      headerComponentParams: {
        shouldExpand: (node: { group?: boolean; key?: string | null }) => {
          if (!node.group) return false
          return isExpandableGroup(node.key)
        }
      },
      minWidth: 300,
      pinned: 'left',
      cellClass: 'ag-group-cell-with-actions',
      cellRendererParams: {
        suppressCount: true,
        suppressDoubleClickExpand: true,
        innerRenderer: StatementRowGroupCell
      }
    }),
    [t, isExpandableGroup]
  )

  const getRowId = useCallback((params: { data: AccountMapGridRow }) => {
    return params.data.rowId
  }, [])

  const getRowStyle = useCallback((params: { node: { group?: boolean } }) => {
    if (params.node.group) {
      return { backgroundColor: '#f5f5f5' }
    }
    return undefined
  }, [])

  const getRowClass = useCallback(
    (params: { node: { group?: boolean; key?: string | null } }) => {
      if (params.node.group && params.node.key) {
        const statementRow = statementRowsById.get(Number(params.node.key))
        if (statementRow?.isParent) {
          return 'ag-row-parent-group'
        }
      }
      return undefined
    },
    [statementRowsById]
  )

  const isGroupOpenByDefault = useCallback(
    (params: { key?: string }) => isExpandableGroup(params.key),
    [isExpandableGroup]
  )

  return {
    getStatementRowName,
    statementRowsById,
    autoGroupColumnDef,
    getRowId,
    getRowStyle,
    getRowClass,
    isGroupOpenByDefault
  }
}
